export type LiveCategoryId =
  | 'lobby'
  | 'baccarat'
  | 'blackjack'
  | 'roulette'
  | 'teenpatti'
  | 'poker'
  | 'table'
  | 'live'

export type LiveCategoryDef = {
  id: LiveCategoryId
  label: string
  filterLabel: string
}

export const LIVE_CATEGORY_ORDER: LiveCategoryDef[] = [
  { id: 'lobby', label: 'LOBBY', filterLabel: 'Lobby' },
  { id: 'baccarat', label: 'BACCARAT', filterLabel: 'Baccarat' },
  { id: 'blackjack', label: 'BLACKJACK', filterLabel: 'Blackjack' },
  { id: 'roulette', label: 'ROULETTE', filterLabel: 'Roulette' },
  { id: 'teenpatti', label: 'TEEN PATTI', filterLabel: 'Teen Patti' },
  { id: 'poker', label: 'POKER', filterLabel: 'Poker' },
  { id: 'table', label: 'TABLE GAMES', filterLabel: 'Table Games' },
  { id: 'live', label: 'LIVE GAMES', filterLabel: 'Live Games' },
]

/**
 * Same 8 Lobby GAMES as dogexch.com/home (recommended lobby cards).
 * Each has a real HUIDU lobby game_uid — launching that uid opens the
 * provider lobby UI (e.g. Ezugi → play.livetables.io lobby), not a table.
 */
export const LIVE_CASINO_LOBBY_GAMES = [
  {
    name: 'Evolution Top Games',
    displayName: 'TOP GAMES LOBBY',
    provider: 'Evolution Live',
    identifier: 'e5df7dfd6dbb9336aa8ef8eeb07c7bc7',
    image: '/imgs/new_casino/topgamesevolobby.webp',
  },
  {
    name: 'Evolution Baccarat',
    displayName: 'BACCARAT LOBBY',
    provider: 'Evolution Live',
    identifier: '7bb3938b6b7e01cc66e797f30faa66dd',
    image: '/imgs/new_casino/baclobbyeco.webp',
  },
  {
    name: 'Evolution Blackjack',
    displayName: 'BLACKJACK LOBBY',
    provider: 'Evolution Live',
    identifier: 'bd61f1feb5ecd2b72e04d22c66651727',
    image: '/imgs/new_casino/jacklobbyevo.webp',
  },
  {
    name: 'Evolution Poker',
    displayName: 'POKER LOBBY',
    provider: 'Evolution Live',
    identifier: '146896890ee326c1f808ef747dd962cf',
    image: '/imgs/new_casino/pokerlobbyevol.webp',
  },
  {
    name: 'Evolution Lobby',
    displayName: 'GAME SHOWS LOBBY',
    provider: 'Evolution Live',
    identifier: '8ef39602e589bf9f32fc351b1cbb338b',
    image: '/imgs/new_casino/evolutionlobby.webp',
  },
  {
    name: 'Ezugi Lobby',
    displayName: 'POPULAR LOBBY',
    provider: 'Ezugi',
    identifier: 'd0e052b031dfcdb08d1803f4bcc618ef',
    image: '/imgs/new_casino/popularezugilobby.png',
  },
  {
    name: 'Lobby',
    displayName: 'MICROGAMING LOBBY',
    provider: 'Microgaming',
    identifier: '4e58131adb95bb061a40e6e309116c19',
    image: '/imgs/new_casino/microgaminglobbynew.jpeg',
  },
  {
    name: 'Playtech Lobby',
    displayName: 'LIVE LOBBY',
    provider: 'Playtech',
    identifier: 'c38efc51028bd65f42396fa079c125d6',
    image: '/imgs/new_casino/laylivelobby.png',
  },
] as const

export function buildLiveCasinoLobbyGames(): Record<string, unknown>[] {
  return LIVE_CASINO_LOBBY_GAMES.map((entry, index) => ({
    _id: entry.identifier,
    game_name: entry.name,
    game_provider: entry.provider,
    game_identifier: entry.identifier,
    identifier: entry.identifier,
    game_images: entry.image,
    game_image: entry.image,
    // dogexch opens these via /casino-int/slots?category=…
    game_slot_status: true,
    isLobby: true,
    lobbyIndex: index + 1,
  }))
}

const LIVE_PROVIDERS = new Set([
  'Evolution Live',
  'Ezugi',
  'Playtech',
  'KM',
  'KingMidas',
  'King Midas',
  'Microgaming',
  'Micro Gaming',
  'KoolBet',
  'Mini',
  '2J',
  'V8',
  'Turbogames',
])

const SLOT_GROUP_RE = /^(slot game|video slots|slots|slot|pop slots|classic games|arcade)$/i

const FALSE_POSITIVE_NAMES = [
  /dragon\s?tiger\s?luck/i,
  /fire\s?lightning/i,
  /platinum\s?lightning/i,
  /lightning\s?bomb/i,
  /baccarat\s?deluxe/i,
  /lightning\s?leopard/i,
  /lightning\s?joker/i,
  /lightning\s?shenlong/i,
]

const CATEGORY_PATTERNS: Partial<Record<LiveCategoryId, RegExp>> = {
  baccarat: /baccarat|bac\s?bo|no\s?commission\s?bac/i,
  roulette: /roulette|rulet|roleta|mini\s?roulette/i,
  blackjack: /black\s?jack|blackjack|\b21\b|twenty\s?one/i,
  poker: /poker|hold\s?em|holdem|texas|caribbean\s?stud|three\s?card/i,
  teenpatti: /teen\s?patti|teenpatti|3\s?patti|andar\s?bahar|andarbahar/i,
  table: /dragon\s?tiger|sic\s?bo|fan\s?tan|fantan|craps|super\s?six|casino\s?war|wheel\s?of/i,
  live: /\blive\b|live\s?casino|live\s?dealer|casino\s?live|casinolive/i,
}

export const LIVE_CASINO_SEARCH_TERMS = [
  'baccarat',
  'roulette',
  'blackjack',
  'poker',
  'teen patti',
  'andar bahar',
  'dragon tiger',
  'sic bo',
  'live casino',
  'live dealer',
  'crazy time',
  'monopoly live',
  'game show',
  'craps',
  'casino holdem',
  'lightning roulette',
  'speed baccarat',
  'immersive roulette',
]

export const LIVE_CASINO_PROVIDERS = [
  'Evolution Live',
  'Ezugi',
  'Playtech',
  'Microgaming',
  'KM',
  'JILI',
  'V8',
  '2J',
  'Mini',
]

function getGameText(game: Record<string, unknown>): string {
  return [
    game.game_name,
    game.game_group,
    game.game_category,
    game.live_category,
    game.slot_category,
    game.provider,
    game.game_provider,
    game.provider_name,
  ]
    .filter(Boolean)
    .join(' ')
}

function getGameId(game: Record<string, unknown>): string {
  return String(game._id || game.game_identifier || game.identifier || '')
}

function isFalsePositiveSlot(game: Record<string, unknown>): boolean {
  const name = String(game.game_name || '')
  const group = String(game.game_group || game.game_category || '')
  if (!SLOT_GROUP_RE.test(group)) return false
  return FALSE_POSITIVE_NAMES.some((rx) => rx.test(name))
}

export function isLiveCasinoGame(game: Record<string, unknown>): boolean {
  if (isFalsePositiveSlot(game)) return false

  const text = getGameText(game)
  const group = String(game.game_group || game.game_category || '')
  const provider = String(game.game_provider || game.provider || game.provider_name || '')

  if (/casinolive|casino\s?live/i.test(group)) return true

  if (LIVE_PROVIDERS.has(provider)) {
    if (SLOT_GROUP_RE.test(group)) {
      return Object.entries(CATEGORY_PATTERNS).some(
        ([key, rx]) => key !== 'live' && !!rx && rx.test(text),
      )
    }
    return true
  }

  if (/table|poker|card|roulette|multiplayer|india poker/i.test(group) && !SLOT_GROUP_RE.test(group)) {
    return Object.values(CATEGORY_PATTERNS).some((rx) => !!rx && rx.test(text))
  }

  return Object.values(CATEGORY_PATTERNS).some((rx) => !!rx && rx.test(text))
}

export function categorizeLiveGame(game: Record<string, unknown>): LiveCategoryId[] {
  const text = getGameText(game)
  const matched: LiveCategoryId[] = []

  for (const { id } of LIVE_CATEGORY_ORDER) {
    if (id === 'lobby') continue
    const rx = CATEGORY_PATTERNS[id]
    if (rx?.test(text)) matched.push(id)
  }

  if (matched.length === 0 && isLiveCasinoGame(game)) {
    matched.push('live')
  }

  return matched
}

export function groupGamesByCategory(games: Record<string, unknown>[]) {
  const groups = Object.fromEntries(
    LIVE_CATEGORY_ORDER.map(({ id }) => [id, [] as Record<string, unknown>[]]),
  ) as Record<LiveCategoryId, Record<string, unknown>[]>

  // Provider lobby tiles (not from game list)
  groups.lobby = buildLiveCasinoLobbyGames()

  for (const game of games) {
    if (!isLiveCasinoGame(game)) continue
    const categories = categorizeLiveGame(game)
    for (const cat of categories) {
      if (cat === 'lobby') continue
      const list = groups[cat]
      if (!list.some((g) => getGameId(g) === getGameId(game))) {
        list.push(game)
      }
    }
  }

  return groups
}

export function mergeUniqueGames(
  arrays: Record<string, unknown>[][]
): Record<string, unknown>[] {
  const seen = new Set<string>()
  const merged: Record<string, unknown>[] = []

  for (const list of arrays) {
    for (const game of list) {
      const id = getGameId(game)
      if (!id || seen.has(id)) continue
      seen.add(id)
      merged.push(game)
    }
  }

  return merged
}

export function filterLiveGamesByQuery(
  games: Record<string, unknown>[],
  query: string
): Record<string, unknown>[] {
  const q = query.trim().toLowerCase()
  if (!q) return games
  return games.filter((game) => getGameText(game).toLowerCase().includes(q))
}
